export const portfolioData = {
  personal: {
    name: "Tilakraj Desai",
    title: "Lead DevOps at Emergent",
    tagline: "Specializing in Cloud Technologies, Automation & Observability Engineering",
    email: "tilakraj.desai@gmail.com",
    location: "Bengaluru, Karnataka",
    linkedIn: "https://www.linkedin.com/in/tilakrajdesai/",
    profilePhoto: "https://customer-assets.emergentagent.com/job_tilak-showcase/artifacts/lfwoiix7_IMG20220903165231~2%20%282%29.jpg",
    summary: "Dedicated DevOps Engineer with over 5+ years of experience in cloud technologies, automation, and observability engineering. Expertise in Kubernetes cluster administration, service deployment, AWS, GCP, and Terraform."
  },

  experience: [
    {
      id: 1,
      company: "Emergent.sh",
      role: "DevOps Lead",
      location: "Remote",
      period: "August 2025 - Current",
      current: true,
      achievements: [
        "Built and lead a lean team of 4 engineers owning the full platform footprint",
        "Operate 30+ Kubernetes clusters along with databases and networking across regions",
        "Designed and rolled out a multi-cloud setup spanning multiple providers",
        "Led ISO 27001 and SOC 2 compliance programs end-to-end",
        "Owned key parts of the platform security posture",
        "Built an internal serverless platform for product teams",
        "Eliminated regional single points of failure to make the platform truly highly available",
        "Built unidirectional tunnels for secure communication between on-premise and cloud environments",
        "Set up in-house observability serving both internal teams and external customers",
        "Established best practices for IaC, security, scaling, and CI/CD across the org",
        "Solved advanced sandboxing problems for running AI agents safely at scale",
        "Solved noisy-neighbor egress network issues in densely bin-packed container environments",
        "Drove cost optimization via event-based scale-down of customer environments",
        "Set up ephemeral environments to enable fast, isolated testing for engineering teams"
      ]
    },
    {
      id: 2,
      company: "Zepto",
      role: "Senior DevOps Engineer",
      location: "Bangalore, Karnataka",
      period: "November 2024 - August 2025",
      current: false,
      achievements: [
        "Introduced event-based workload scaling in Kubernetes using KEDA for enhanced responsiveness and optimized resource utilization",
        "Deployed a self-hosted LLM integrated with Grafana for real-time, AI-assisted explanations of profiling data",
        "Deployed Consul OSS as a key-value store for dynamic configuration and service discovery",
        "Integrated Argo Rollouts with Prometheus using AnalysisTemplates for automated canary analysis and rollback based on real-time metrics",
        "Built in-house observability platforms to support massive telemetry volumes and optimize operational costs, reducing reliance on SaaS tools",
        "Engineered a distributed tracing solution using Grafana Tempo to improve developer workflows",
        "Scaled the centralized logging platform using Grafana Loki to support ingestion of up to 24TB of logs daily",
        "Piloted and productionized standardized OpenTelemetry auto-instrumentation (compilation time, SDK-based, eBPF-based)",
        "Implemented continuous profiling using Pyroscope to quickly identify and address performance bottlenecks",
        "Led the migration from Thanos to Grafana Mimir for a scalable, cost-effective metrics platform with granular namespace control"
      ]
    },
    {
      id: 3,
      company: "Groww",
      role: "Senior DevOps Engineer",
      location: "Bangalore, Karnataka",
      period: "April 2024 - November 2024",
      current: false,
      achievements: [
        "Built a HA, fault-tolerant, and scalable observability setup (Prometheus, Grafana, Mimir, Alertmanager, OTEL collector, Loki, Tempo) for a large-scale distributed system",
        "Re-designed the alerting platform to be self-service for developers",
        "Reduced observability stack infrastructure costs by half",
        "Designed and deployed a tracing setup for microservices, enabling faster bottleneck identification and easier SLO implementation",
        "Implemented Crossplane for GKE nodepools (Infrastructure as Code)"
      ]
    },
    {
      id: 4,
      company: "OutSystems",
      role: "SRE",
      location: "Remote",
      period: "May 2022 - April 2024",
      current: false,
      achievements: [
        "Spearheaded Kubernetes cluster administration for observability components, ensuring seamless integration and performance optimization",
        "Created Python/Lambda automation to flag and delete unused infrastructure, saving significant costs",
        "Orchestrated Terraform deployments for Infrastructure as Code (IaC)",
        "Led the development of key setups, including Configuration Backup automation for managed services",
        "Standardized the metric onboarding process to reduce metric bombing",
        "Migrated Prometheus to agent mode to enhance observability stack reliability"
      ]
    },
    {
      id: 5,
      company: "Crest Data Systems",
      role: "SRE",
      location: "Ahmedabad",
      period: "June 2020 - April 2022",
      current: false,
      achievements: [
        "Applied expertise in system architecture, deployment, and monitoring for scalable and highly available solutions",
        "Developed and implemented incident response and disaster recovery plans",
        "Conducted performance analysis, identified bottlenecks, and implemented optimization solutions",
        "Served as a subject matter expert, providing technical guidance and support to clients"
      ]
    }
  ],

  skills: {
    cloud: ["AWS", "GCP"],
    orchestration: ["Kubernetes", "Helm", "ArgoCD", "Karpenter"],
    observability: ["Prometheus", "Grafana", "Grafana Mimir", "Grafana Loki", "Grafana Tempo", "Alertmanager", "OpenTelemetry", "Pyroscope"],
    iac: ["Terraform", "Crossplane"],
    automation: ["Github Workflows", "Python", "Shell"],
    tools: ["KEDA", "OpenCost", "Consul OSS", "Linux"]
  },

  projects: [
    {
      id: 1,
      title: "In-house Observability Platform",
      description: "Built comprehensive observability platforms to support massive telemetry volumes and optimize operational costs, reducing reliance on SaaS tools.",
      technologies: ["Prometheus", "Grafana", "Mimir", "Loki", "Tempo"],
      metrics: ["24TB logs daily", "Reduced SaaS costs"]
    },
    {
      id: 2,
      title: "Distributed Tracing Solution",
      description: "Engineered distributed tracing using Grafana Tempo to improve developer workflows and enable faster bottleneck identification.",
      technologies: ["Grafana Tempo", "OpenTelemetry", "Kubernetes"],
      metrics: ["Improved debugging time", "Enhanced SLO implementation"]
    },
    {
      id: 3,
      title: "Metrics Platform Migration",
      description: "Led migration from Thanos to Grafana Mimir for a scalable, cost-effective metrics platform with granular namespace control.",
      technologies: ["Grafana Mimir", "Thanos", "Prometheus"],
      metrics: ["Cost optimization", "Improved scalability"],
      blogLink: "#"
    },
    {
      id: 4,
      title: "Event-based Workload Scaling",
      description: "Introduced event-based workload scaling in Kubernetes using KEDA for enhanced responsiveness and optimized resource utilization.",
      technologies: ["KEDA", "Kubernetes", "Event-driven architecture"],
      metrics: ["Optimized resource usage", "Enhanced responsiveness"]
    },
    {
      id: 5,
      title: "Self-service Alerting Platform",
      description: "Re-designed alerting platform to enable developers to manage their own alerts, improving autonomy and reducing operational overhead.",
      technologies: ["Alertmanager", "Prometheus", "Grafana"],
      metrics: ["50% cost reduction", "Developer self-service"]
    },
    {
      id: 6,
      title: "Infrastructure Cost Optimization",
      description: "Created Python/Lambda automation to flag and delete unused infrastructure, achieving significant cost savings.",
      technologies: ["Python", "AWS Lambda", "Cost optimization"],
      metrics: ["Significant cost savings", "Automated cleanup"]
    }
  ],

  education: {
    institution: "Dharmsinh Desai University",
    degree: "B.Tech (Information Technology)",
    location: "Nadiad, India",
    period: "August 2016 - May 2020"
  },

  blog: {
    featured: {
      title: "Migration from Thanos to Grafana Mimir",
      description: "Deep dive into our journey of migrating metrics infrastructure for improved scalability and cost efficiency.",
      link: "#"
    }
  },

  openSource: [
    {
      id: 1,
      project: "alibaba/loongsuite-go-agent",
      role: "Contributor",
      description: "OpenTelemetry compile-time auto-instrumentation for Go. Contributed gRPC status-code attribute extraction for RPC metrics instrumentation, including the implementation, unit tests, and metrics-conversion wiring.",
      highlights: [
        "Added rpc.grpc.status_code attribute to gRPC metrics instrumentation",
        "Authored test suite covering success, gRPC errors, non-gRPC errors and non-gRPC systems",
        "Updated rpc metrics conversion map to surface the new attribute"
      ],
      link: "https://github.com/alibaba/loongsuite-go-agent/commit/138ceeac19a1150e544669364c275265f6642672",
      tags: ["Go", "OpenTelemetry", "gRPC", "Observability"]
    },
    {
      id: 2,
      project: "helm/charts (community charts)",
      role: "Contributor",
      description: "Multiple fixes and feature additions across community Helm charts to improve reliability, configurability, and day-2 operability for downstream operators.",
      highlights: [
        "Shipped bug fixes and feature PRs in widely-used community Helm charts",
        "Improved chart configurability and upgrade safety for production users"
      ],
      link: "https://github.com/helm/charts",
      tags: ["Helm", "Kubernetes", "Open Source"]
    }
  ]
};
